import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import MapPicker from "@/components/checkout/MapPicker";
import AddressBook from "@/components/checkout/AddressBook";
import PinConfirmModal from "@/components/checkout/PinConfirmModal";
import { loadAddresses, saveAddress, removeAddress } from "@/lib/addressBook";

const inputCls =
  "w-full bg-transparent border-b border-line py-3 focus:outline-none focus:border-gold placeholder:text-espresso-light/70 text-espresso";

const FAMILY_TUB_PREORDER_SLUGS = new Set([
  "family-tub-classic-eggless",
  "family-tub-traditional-italian",
  "family-tub-biscoff",
]);
const PREORDER_NOTICE_MS = 24 * 60 * 60 * 1000;

function formatIndiaDate(date) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function formatIndiaTime(date) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value;
  return `${get("hour")}:${get("minute")}`;
}

function plusDays(date, days) {
  const noon = new Date(`${date}T12:00:00Z`);
  noon.setUTCDate(noon.getUTCDate() + days);
  return noon.toISOString().slice(0, 10);
}

function earliestPreorderSlot(slots) {
  const threshold = new Date(Date.now() + PREORDER_NOTICE_MS);
  const minimumDate = formatIndiaDate(threshold);
  const minimumTime = formatIndiaTime(threshold);
  for (let dayOffset = 0; dayOffset < 14; dayOffset += 1) {
    const date = plusDays(minimumDate, dayOffset);
    const eligibleSlots = slots.filter((slot) => date > minimumDate || slot >= minimumTime);
    if (eligibleSlots.length) {
      return { date, time: eligibleSlots[0], minimumDate, minimumTime };
    }
  }
  return { date: minimumDate, time: "", minimumDate, minimumTime };
}

function todayISO() {
  const d = new Date();
  d.setDate(d.getDate());
  return d.toISOString().slice(0, 10);
}

function buildSlots(open, close, interval) {
  // open/close "HH:MM"; if close < open, close is next day
  const [oh, om] = open.split(":").map(Number);
  const [ch, cm] = close.split(":").map(Number);
  let start = oh * 60 + om;
  let end = ch * 60 + cm;
  if (end <= start) end += 24 * 60;
  const out = [];
  for (let m = start; m <= end - interval; m += interval) {
    const t = m % (24 * 60);
    const hh = String(Math.floor(t / 60)).padStart(2, "0");
    const mm = String(t % 60).padStart(2, "0");
    out.push(`${hh}:${mm}`);
  }
  return out;
}

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  // Login gate — customers must sign in to check out. Once logged in, we
  // prefill contact + address from their saved profile.
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login?next=/checkout", { replace: true });
    }
  }, [authLoading, user, navigate]);
  const [fulfillment, setFulfillment] = useState("delivery");
  const [customer, setCustomer] = useState({
    full_name: "", mobile: "", email: "",
    address_line1: "", address_line2: "", area: "",
    city: "Ghaziabad", state: "", pincode: "", landmark: "", instructions: "",
    lat: null, lng: null, geo_source: null,
  });
  const mapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [savedAddresses, setSavedAddresses] = useState(() => loadAddresses());
  const [liveQuote] = [null]; // legacy — carrier quotes removed; kept as no-op for any downstream reads
  const [showPinConfirm, setShowPinConfirm] = useState(false);
  const [slotDate, setSlotDate] = useState(todayISO());
  const [slotTime, setSlotTime] = useState("ASAP");
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null); // { code, discount }
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  const [deliveryCfg, setDeliveryCfg] = useState(null);
  const [timingsCfg, setTimingsCfg] = useState(null);
  const [pinChecking, setPinChecking] = useState(false);
  const [pinOk, setPinOk] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [placed, setPlaced] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  const [applyReward, setApplyReward] = useState(false);
  const [referralCode, setReferralCode] = useState(() => {
    return localStorage.getItem("tco_referral_code") || "";
  });
  const [kitchenNotes, setKitchenNotes] = useState("");
  const [paymentCfg, setPaymentCfg] = useState(null);
  const hasFamilyTubPreorder = useMemo(() => {
    return items.some((item) => FAMILY_TUB_PREORDER_SLUGS.has(item.slug));
  }, [items]);

  useEffect(() => {
    api.get("/config/delivery").then(({ data }) => setDeliveryCfg(data));
    api.get("/config/timings").then(({ data }) => setTimingsCfg(data));
    api.get("/config/payment").then(({ data }) => setPaymentCfg(data)).catch(() => {});
    // Prefill from saved profile — phone + last delivery address.
    api.get("/auth/profile").then(({ data }) => {
      if (!data?.authenticated) return;
      setCustomer((prev) => ({
        ...prev,
        full_name: prev.full_name || data.name || "",
        email: prev.email || data.email || "",
        mobile: prev.mobile || data.phone || "",
        ...(data.saved_address ? {
          address_line1: prev.address_line1 || data.saved_address.address_line1 || "",
          address_line2: prev.address_line2 || data.saved_address.address_line2 || "",
          area: prev.area || data.saved_address.area || "",
          city: prev.city || data.saved_address.city || "Ghaziabad",
          state: prev.state || data.saved_address.state || "",
          pincode: prev.pincode || data.saved_address.pincode || "",
          landmark: prev.landmark || data.saved_address.landmark || "",
          lat: prev.lat ?? data.saved_address.lat ?? null,
          lng: prev.lng ?? data.saved_address.lng ?? null,
        } : {}),
      }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length === 0 && !placed) navigate("/menu");
  }, [items, navigate, placed]);

  // Fetch loyalty when a plausible email is entered
  useEffect(() => {
    const em = customer.email?.trim();
    if (!em || !em.includes("@") || !em.includes(".")) { setLoyalty(null); return; }
    const t = setTimeout(() => {
      api.get(`/loyalty`, { params: { email: em } })
        .then(({ data }) => setLoyalty(data))
        .catch(() => setLoyalty(null));
    }, 400);
    return () => clearTimeout(t);
  }, [customer.email]);

  // Live delivery quote — distance-slab pricing. Runs whenever the pinned
  // lat/lng, cart subtotal, or fulfillment changes so the fee + serviceability
  // status stay in lock-step with reality. Debounced by 300ms to avoid a
  // request storm while the user drags the map pin.
  useEffect(() => {
    if (fulfillment !== "delivery") { setDeliveryQuote(null); return; }
    if (customer.lat == null || customer.lng == null) {
      setDeliveryQuote({ is_serviceable: false, reason: "no_pin" });
      return;
    }
    const handle = setTimeout(() => {
      api.post("/delivery/quote", {
        fulfillment,
        subtotal,
        customer: { lat: customer.lat, lng: customer.lng, pincode: customer.pincode },
      }).then(({ data }) => setDeliveryQuote(data)).catch(() => {
        setDeliveryQuote({
          is_serviceable: false,
          reason: "distance_unavailable",
          message: "We could not verify road distance for this location.",
        });
      });
    }, 300);
    return () => clearTimeout(handle);
  }, [fulfillment, subtotal, customer.lat, customer.lng, customer.pincode]);

  const deliveryFee = useMemo(() => {
    if (fulfillment !== "delivery") return 0;
    return Number(deliveryQuote?.fee_inr || 0);
  }, [deliveryQuote, fulfillment]);
  const outOfRange = fulfillment === "delivery" && deliveryQuote?.reason === "out_of_range";
  const distanceUnavailable = fulfillment === "delivery" && deliveryQuote?.reason === "distance_unavailable";
  const belowMinOrder = fulfillment === "delivery" && deliveryQuote?.reason === "below_min_order";
  const pinNotSet = fulfillment === "delivery" && deliveryQuote?.reason === "no_pin";

  // Exclusive GST — 2.5% CGST + 2.5% SGST computed on the taxable amount
  // (subtotal minus promo discount). Kept in sync with backend `_compute_order`.
  const taxableAmount = useMemo(
    () => Math.max(0, subtotal - (promo?.discount || 0)),
    [subtotal, promo]
  );
  const cgst = useMemo(() => Math.round(taxableAmount * 0.025 * 100) / 100, [taxableAmount]);
  const sgst = cgst;
  const total = Math.max(0, taxableAmount + cgst + sgst + deliveryFee);

  const slots = useMemo(() => {
    if (!timingsCfg) {
      return [];
    }
    return buildSlots(
      timingsCfg.open_time,
      timingsCfg.close_time,
      timingsCfg.slot_interval_minutes || 30,
    );
  }, [timingsCfg]);
  const preorderMinimum = useMemo(() => earliestPreorderSlot(slots), [slots]);
  const visibleSlots = hasFamilyTubPreorder
    ? slots.filter((slot) => {
      return slotDate > preorderMinimum.minimumDate || slot >= preorderMinimum.minimumTime;
    })
    : slots;

  useEffect(() => {
    if (!hasFamilyTubPreorder || !preorderMinimum.time) {
      return;
    }
    const validDate = slotDate >= preorderMinimum.minimumDate;
    const validTime = slotDate > preorderMinimum.minimumDate
      ? slotTime !== "ASAP"
      : slotTime !== "ASAP" && slotTime >= preorderMinimum.minimumTime;
    if (!validDate || !validTime) {
      setSlotDate(preorderMinimum.date);
      setSlotTime(preorderMinimum.time);
    }
  }, [
    hasFamilyTubPreorder,
    preorderMinimum.date,
    preorderMinimum.minimumDate,
    preorderMinimum.minimumTime,
    preorderMinimum.time,
    slotDate,
    slotTime,
  ]);

  const handleLocationPick = useCallback((loc) => {
    setCustomer((prev) => ({
      ...prev,
      lat: loc.lat,
      lng: loc.lng,
      geo_source: loc.geo_source,
      // Only overwrite text fields that came back non-empty from the geocoder,
      // so we never wipe what the user typed manually.
      address_line1: loc.address_line1 || prev.address_line1,
      city: loc.city || prev.city,
      state: loc.state || prev.state,
      pincode: loc.pincode || prev.pincode,
    }));
    // A newly picked location should retrigger serviceability check.
    if (loc.pincode) setPinOk(null);
  }, []);

  const handlePickSaved = useCallback((a) => {
    setCustomer((prev) => ({
      ...prev,
      address_line1: a.address_line1 || "",
      address_line2: a.address_line2 || "",
      area: a.area || "",
      city: a.city || prev.city,
      state: a.state || "",
      pincode: a.pincode || "",
      landmark: a.landmark || "",
      instructions: a.instructions || prev.instructions,
      lat: typeof a.lat === "number" ? a.lat : null,
      lng: typeof a.lng === "number" ? a.lng : null,
      geo_source: a.geo_source || null,
    }));
    setPinOk(null);
    toast.success("Address filled in");
  }, []);

  const handleRemoveSaved = useCallback((id) => {
    setSavedAddresses(removeAddress(id));
  }, []);

  const checkPincode = async () => {
    if (!customer.pincode) return;
    setPinChecking(true); setPinOk(null);
    try {
      const { data } = await api.get(`/check-pincode/${customer.pincode}`);
      setPinOk(data.serviceable);
      if (!data.serviceable) {
        toast.error("We don't deliver to this pincode yet.");
      } else {
        toast.success("Delivery available.");
      }
    } catch { setPinOk(false); }
    finally { setPinChecking(false); }
  };

  const applyPromo = async () => {
    if (!promoInput) return;
    try {
      const { data } = await api.post("/promotions/validate", { code: promoInput, subtotal });
      setPromo(data);
      toast.success(`Promo applied — ₹${data.discount.toFixed(0)} off`);
    } catch (e) {
      setPromo(null);
      toast.error(e?.response?.data?.detail || "Invalid promo");
    }
  };

  const placeOrder = async () => {
    if (!customer.full_name || !customer.mobile || !customer.email) {
      toast.error("Please fill your name, mobile and email.");
      return;
    }
    if (fulfillment === "delivery" && (!customer.address_line1 || !customer.pincode)) {
      toast.error("Please provide your delivery address and pincode.");
      return;
    }
    if (hasFamilyTubPreorder && (!slotDate || !slotTime || slotTime === "ASAP")) {
      toast.error("Choose a Family Tub delivery date and time at least 24 hours ahead.");
      return;
    }
    // Delivery orders go through one final pin-confirm step. Guest can still
    // proceed without a pin (with a soft warning) — controlled inside the modal.
    if (fulfillment === "delivery" && !showPinConfirm) {
      setShowPinConfirm(true);
      return;
    }
    setShowPinConfirm(false);
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((it) => ({
          product_id: it.id, quantity: it.quantity,
          flavor: it.flavor || null, selections: it.selections || [],
          is_referral_reward: Boolean(it.is_referral_reward),
        })),
        fulfillment,
        customer,
        slot_date: slotDate,
        slot_time: slotTime,
        promo_code: promo?.code || null,
        apply_reward: applyReward && (loyalty?.pending_rewards || 0) > 0,
        referral_code: referralCode.trim() || null,
        referral_reward_id: items.find((item) => item.is_referral_reward)?.referral_reward_id || null,
        kitchen_notes: kitchenNotes.trim() || null,
      };
      const { data: order } = await api.post("/orders", payload);
      setPlaced(true);
      // Save this delivery address into the customer's address book for
      // next time. Skip for pickup orders (no address to save).
      if (fulfillment === "delivery" && customer.address_line1 && customer.pincode) {
        try { setSavedAddresses(saveAddress(customer)); } catch (_) {}
      }

      if (order.razorpay_order_id && window.Razorpay && paymentCfg?.key_id) {
        const rzp = new window.Razorpay({
          key: paymentCfg.key_id,
          amount: Math.round(order.total * 100),
          currency: "INR",
          name: "TIRAMISÙ & Co.",
          description: `Order ${order.order_number}`,
          order_id: order.razorpay_order_id,
          prefill: { name: customer.full_name, email: customer.email, contact: customer.mobile },
          theme: { color: "#2A1C14" },
          handler: async (res) => {
            await api.post("/orders/verify-payment", {
              order_id: order.id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            navigate(`/order/${order.order_number}`, { replace: true });
            clear();
          },
          modal: { ondismiss: () => setSubmitting(false) },
        });
        rzp.open();
      } else {
        // No gateway configured — proceed with pending payment (owner confirms manually)
        await api.post("/orders/verify-payment", { order_id: order.id });
        navigate(`/order/${order.order_number}`, { replace: true });
        clear();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Order failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="checkout-page" className="max-w-[1400px] mx-auto px-4 md:px-10 py-8 md:py-16 pb-32 md:pb-16">
      <div className="label-xs text-espresso-light">Checkout</div>
      <h1 className="mt-3 font-serif text-4xl md:text-6xl leading-[0.95] tracking-tight text-espresso">Almost there.</h1>

      <div className="mt-8 grid gap-8 md:mt-10 md:grid-cols-12 md:gap-10">
        <div className="space-y-9 md:col-span-8 md:space-y-14">
          <section>
            <div className="label-xs text-espresso-light mb-4">01 · Fulfillment</div>
            <div className="grid grid-cols-2 gap-3">
              {["delivery","pickup"].map((f) => (
                <button
                  key={f}
                  onClick={() => setFulfillment(f)}
                  data-testid={`fulfillment-${f}`}
                  className={`border p-4 text-left transition-colors duration-200 md:p-6 ${fulfillment===f ? "border-espresso bg-espresso text-ivory" : "border-line text-espresso"}`}
                >
                  <div className="label-xs">{f}</div>
                  <div className="font-serif text-xl mt-2">
                    {f==="delivery" ? "To your door" : "From our kitchen"}
                  </div>
                </button>
              ))}
            </div>
          </section>

          <section className="scroll-mt-24">
            <div className="label-xs text-espresso-light mb-4">02 · Your details</div>
            <div className="grid md:grid-cols-2 gap-6">
              <input data-testid="cust-name" placeholder="Full name" className={inputCls} value={customer.full_name} onChange={(e)=>setCustomer({...customer, full_name:e.target.value})} />
              <input data-testid="cust-mobile" placeholder="Mobile number" className={inputCls} value={customer.mobile} onChange={(e)=>setCustomer({...customer, mobile:e.target.value})} />
              <input data-testid="cust-email" placeholder="Email" type="email" className={`${inputCls} md:col-span-2`} value={customer.email} onChange={(e)=>setCustomer({...customer, email:e.target.value})} />
            </div>
          </section>

          {fulfillment === "delivery" && (
            <section>
              <div className="label-xs text-espresso-light mb-4">03 · Delivery address</div>
              <AddressBook
                addresses={savedAddresses}
                onPick={handlePickSaved}
                onRemove={handleRemoveSaved}
              />
              <div className="mb-6">
                <MapPicker
                  apiKey={mapsApiKey}
                  value={customer.lat && customer.lng ? { lat: customer.lat, lng: customer.lng } : null}
                  onLocationChange={handleLocationPick}
                />
                {customer.lat && customer.lng && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-espresso-light" data-testid="map-pin-set">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-gold"></span>
                    Pin set · {customer.lat.toFixed(5)}, {customer.lng.toFixed(5)}
                    {customer.geo_source === "gps" && <span className="italic">· via GPS</span>}
                    {customer.geo_source === "map_drag" && <span className="italic">· dragged</span>}
                    {customer.geo_source === "search" && <span className="italic">· searched</span>}
                    {customer.geo_source === "map_tap" && <span className="italic">· tapped</span>}
                  </div>
                )}
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <input data-testid="addr-line1" placeholder="Apartment / House number" className={inputCls} value={customer.address_line1} onChange={(e)=>setCustomer({...customer, address_line1:e.target.value})} />
                <input data-testid="addr-line2" placeholder="Street / Society" className={inputCls} value={customer.address_line2} onChange={(e)=>setCustomer({...customer, address_line2:e.target.value})} />
                <input data-testid="addr-area" placeholder="Area / Locality" className={inputCls} value={customer.area} onChange={(e)=>setCustomer({...customer, area:e.target.value})} />
                <input data-testid="addr-city" placeholder="City" className={inputCls} value={customer.city} onChange={(e)=>setCustomer({...customer, city:e.target.value})} />
                <div className="flex items-end gap-3 flex-wrap sm:flex-nowrap">
                  <input data-testid="addr-pincode" placeholder="Pincode" className={`${inputCls} flex-1 min-w-0`} value={customer.pincode} onChange={(e)=>{setCustomer({...customer, pincode:e.target.value}); setPinOk(null);}} />
                  <button className="btn-outline whitespace-nowrap" onClick={checkPincode} disabled={pinChecking} data-testid="check-pincode-btn">
                    {pinChecking?"Checking…":"Check"}
                  </button>
                </div>
                <input data-testid="addr-landmark" placeholder="Landmark (optional)" className={inputCls} value={customer.landmark} onChange={(e)=>setCustomer({...customer, landmark:e.target.value})} />
                <textarea rows="2" data-testid="addr-instructions" placeholder="Delivery instructions (optional)" className={`${inputCls} md:col-span-2`} value={customer.instructions} onChange={(e)=>setCustomer({...customer, instructions:e.target.value})} />
              </div>
              {pinOk === true && <div className="text-sm text-espresso-light mt-3" data-testid="pincode-ok">Delivery available for this pincode.</div>}
              {pinOk === false && (
                <div className="text-sm text-destructive mt-3 space-y-1" data-testid="pincode-not-serviceable">
                  <div>We can't deliver to this pincode yet.</div>
                  <div className="text-espresso-light">Try Store pickup instead, or reach us at <a className="link-underline" href="mailto:hello@tiramisuandco.in">hello@tiramisuandco.in</a> if you're just outside our zone.</div>
                </div>
              )}
            </section>
          )}

          {fulfillment === "pickup" && deliveryCfg && (
            <section>
              <div className="label-xs text-espresso-light mb-4">03 · Pickup</div>
              <div className="border border-line p-6">
                <div className="font-serif text-xl text-espresso">{deliveryCfg.pickup_address}</div>
                <p className="text-espresso-light text-sm mt-2">{deliveryCfg.pickup_instructions}</p>
              </div>
            </section>
          )}

          <section>
            <div className="label-xs text-espresso-light mb-4">
              04 · {hasFamilyTubPreorder
                ? "Family Tub pre-order"
                : fulfillment === "delivery"
                  ? "Delivery"
                  : "Pickup"} slot
            </div>
            {hasFamilyTubPreorder && (
              <div
                className="mb-5 border-l-2 border-gold bg-gold/5 px-4 py-3 text-sm text-espresso"
                data-testid="checkout-family-tub-preorder-notice"
              >
                PRE-ORDER — Select a delivery time at least 24 hours from now. Same-day delivery
                is not available for Family Tubs.
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-6">
              <input
                type="date"
                min={hasFamilyTubPreorder ? preorderMinimum.minimumDate : todayISO()}
                value={slotDate}
                onChange={(event) => setSlotDate(event.target.value)}
                className={inputCls}
                data-testid="slot-date"
                required={hasFamilyTubPreorder}
              />
              <select
                value={slotTime}
                onChange={(event) => setSlotTime(event.target.value)}
                className={inputCls}
                data-testid="slot-time"
                required={hasFamilyTubPreorder}
              >
                {!hasFamilyTubPreorder && timingsCfg?.allow_asap && (
                  <option value="ASAP">ASAP (within 90 min)</option>
                )}
                {visibleSlots.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </section>

          {loyalty && (loyalty.pending_rewards > 0 || loyalty.paid_count > 0) && (
            <section data-testid="loyalty-panel" className="border border-gold/40 bg-gold/5 p-6">
              <div className="label-xs text-gold-hover">Loyalty</div>
              {loyalty.pending_rewards > 0 ? (
                <div className="mt-3">
                  <div className="font-serif text-2xl text-espresso">
                    You have a free <span className="italic">Traditional Italian</span> cup waiting.
                  </div>
                  <label className="mt-4 flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={applyReward}
                      onChange={(e) => setApplyReward(e.target.checked)}
                      data-testid="apply-reward-checkbox"
                      className="w-4 h-4 accent-gold"
                    />
                    <span className="text-sm text-espresso">Add it to this order — on the house.</span>
                  </label>
                </div>
              ) : (
                <div className="mt-3 text-sm text-espresso-light">
                  {loyalty.paid_count} paid order{loyalty.paid_count === 1 ? "" : "s"} so far.
                  {" "}<span className="text-espresso">{loyalty.orders_until_reward}</span> more and a free Traditional Italian is yours.
                </div>
              )}
            </section>
          )}

          <section>
            <div className="label-xs text-espresso-light mb-4">05 · Promo &amp; Referral</div>
            <div className="flex gap-3 max-w-md">
              <input placeholder="Enter promo code" className={inputCls} value={promoInput} onChange={(e)=>setPromoInput(e.target.value)} data-testid="promo-input" />
              <button className="btn-outline" onClick={applyPromo} data-testid="promo-apply">Apply</button>
            </div>
            {promo && <div className="mt-3 text-sm text-espresso-light">Promo <span className="text-espresso font-medium">{promo.code}</span> — ₹{promo.discount.toFixed(0)} off</div>}
            <div className="mt-6 max-w-md">
              <input
                placeholder="Referral code (optional) — e.g. TCOABCDEF"
                className={inputCls}
                value={referralCode}
                onChange={(e) => {
                  const code = e.target.value.toUpperCase();
                  setReferralCode(code);
                  if (code) localStorage.setItem("tco_referral_code", code);
                  else localStorage.removeItem("tco_referral_code");
                }}
                data-testid="referral-input"
              />
              <p className="mt-2 text-xs text-espresso-light">
                Signed-in first orders over ₹399 receive 10% off. Your friend earns a cup after 3 paid, delivered referrals.
              </p>
            </div>
          </section>

          <section>
            <div className="label-xs text-espresso-light mb-4">06 · Message the kitchen</div>
            <textarea
              rows="3"
              placeholder="A note for our team — gifting, allergens, delivery quirks. Optional."
              className={inputCls}
              value={kitchenNotes}
              onChange={(e) => setKitchenNotes(e.target.value)}
              maxLength={400}
              data-testid="kitchen-notes"
            />
            <p className="mt-2 text-xs text-espresso-light text-right">{kitchenNotes.length}/400</p>
          </section>
        </div>

        <aside className="h-fit border border-line p-5 md:sticky md:top-28 md:col-span-4 md:p-8">
          <div className="flex items-center justify-between">
            <div className="label-xs text-espresso-light">Order summary</div>
            <button
              type="button"
              onClick={() => navigate("/menu")}
              className="text-xs uppercase tracking-wider text-gold-hover hover:text-espresso underline underline-offset-4 transition-colors"
              data-testid="shop-more-btn"
            >
              + Shop More
            </button>
          </div>
          <div className="mt-6 space-y-3">
            {items.map((it) => {
              const grouped = it.selections?.length && it.category === "boxes"
                ? Object.entries(it.selections.reduce((a, s) => ({ ...a, [s]: (a[s] || 0) + 1 }), {}))
                : null;
              return (
                <div key={it._key} className="text-sm">
                  <div className="flex justify-between">
                    <span className="text-espresso">{it.name}{it.flavor?` · ${it.flavor}`:""} × {it.quantity}</span>
                    <span className="text-espresso">₹{it.price*it.quantity}</span>
                  </div>
                  {grouped && (
                    <ul className="mt-1 pl-3 text-xs text-espresso-light">
                      {grouped.map(([f, n]) => <li key={f}>· {f} × {n}</li>)}
                    </ul>
                  )}
                  {it.savings > 0 && (
                    <div className="mt-1 pl-3 flex justify-between text-xs text-gold-hover" data-testid={`checkout-savings-${it.slug}`}>
                      <span>Bundle savings</span>
                      <span>−₹{it.savings * it.quantity}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <hr className="editorial-hr my-5" />
          <div className="space-y-2 text-espresso">
            <div className="flex justify-between"><span>Items</span><span data-testid="checkout-subtotal">₹{subtotal}</span></div>
            {promo && promo.discount > 0 && (
              <div className="flex justify-between text-gold-hover">
                <span>Discount ({promo.code})</span>
                <span>−₹{promo.discount.toFixed(0)}</span>
              </div>
            )}
            {/* Exclusive GST — 2.5% CGST + 2.5% SGST on food subtotal (net
                of promo discount). Kept as separate line items so the
                invoice is intra-state GST compliant. */}
            <div className="flex justify-between">
              <span>CGST (2.5%)</span>
              <span data-testid="checkout-cgst">₹{cgst.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>SGST (2.5%)</span>
              <span data-testid="checkout-sgst">₹{sgst.toFixed(2)}</span>
            </div>
            {fulfillment === "delivery" && (
              <div className="flex items-center justify-between">
                <span>Delivery{deliveryQuote?.distance_km ? ` · Road ${deliveryQuote.distance_km.toFixed(1)} km` : ""}</span>
                <span data-testid="delivery-fee">{deliveryFee > 0 ? `₹${deliveryFee}` : "—"}</span>
              </div>
            )}
          </div>
          <hr className="editorial-hr my-5" />
          {/* Distance-slab serviceability notices */}
          {outOfRange && (
            <div className="mb-4 border border-destructive/60 bg-destructive/5 px-3 py-2.5 text-sm text-destructive" data-testid="delivery-out-of-range">
              Delivery is not available to your location. Road distance exceeds 20 km.
            </div>
          )}
          {distanceUnavailable && (
            <div className="mb-4 border border-destructive/60 bg-destructive/5 px-3 py-2.5 text-sm text-destructive" data-testid="delivery-distance-unavailable">
              We could not verify road distance for this location. Please try again.
            </div>
          )}
          {belowMinOrder && (
            <div className="mb-4 border border-gold/60 bg-gold/5 px-3 py-2.5 text-sm text-espresso" data-testid="delivery-below-min">
              Add <span className="font-medium text-gold-hover">₹{(deliveryQuote?.shortfall || 0).toFixed(0)}</span> more to continue.
              <div className="text-xs text-espresso-light mt-0.5">
                Minimum for {deliveryQuote?.band_max_km} km delivery: ₹{(deliveryQuote?.min_order_subtotal || 0).toFixed(0)}
              </div>
              <button
                type="button"
                onClick={() => navigate("/menu")}
                data-testid="add-more-items-btn"
                className="btn-outline w-full mt-3"
              >
                Add More Items
              </button>
            </div>
          )}
          {pinNotSet && customer.lat == null && (
            <div className="mb-4 border border-line px-3 py-2.5 text-sm text-espresso-light" data-testid="delivery-no-pin">
              Drop a pin on the map to see your delivery fee.
            </div>
          )}
          <div className="flex justify-between items-baseline">
            <span className="label-xs">Total</span>
            <span className="font-serif text-3xl text-espresso" data-testid="checkout-total">₹{total.toFixed(0)}</span>
          </div>
          <button
            onClick={placeOrder}
            disabled={submitting || outOfRange || distanceUnavailable || belowMinOrder || pinNotSet}
            data-testid="place-order-btn"
            className="btn-espresso w-full mt-8 disabled:opacity-40"
          >
            {submitting ? "Placing…" : (paymentCfg?.configured ? "Pay securely" : "Place order")}
          </button>
          {paymentCfg?.configured ? (
            <div className="mt-4 space-y-2" data-testid="payment-info">
              <div className="flex items-center justify-center gap-3 text-[10px] uppercase tracking-[0.18em] text-espresso-light">
                <span>UPI</span><span className="text-line">·</span>
                <span>Cards</span><span className="text-line">·</span>
                <span>Netbanking</span><span className="text-line">·</span>
                <span>Wallets</span>
              </div>
              <p className="text-xs text-espresso-light text-center">
                Secured by Razorpay
                {paymentCfg.test_mode && (
                  <span className="ml-2 inline-flex items-baseline px-2 py-0.5 border border-gold/50 text-gold-hover text-[10px] uppercase tracking-wider" data-testid="test-mode-badge">
                    Test mode
                  </span>
                )}
              </p>
            </div>
          ) : (
            <p className="text-xs text-espresso-light mt-4 italic text-center">
              Payments are being finalised — we'll confirm your order and share payment details on WhatsApp.
            </p>
          )}
          <p className="text-xs text-espresso-light mt-4">By placing this order you agree to our terms and refund policy.</p>
        </aside>
      </div>

      {/* Mobile-only sticky pay bar */}
      <div
        data-testid="mobile-pay-bar"
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-ivory/95 backdrop-blur-md border-t border-line px-4 py-3 flex items-center justify-between gap-4"
      >
        <div className="flex flex-col">
          <span className="label-xs text-espresso-light">Total</span>
          <span className="font-serif text-2xl text-espresso leading-none">₹{total.toFixed(0)}</span>
        </div>
        <button
          onClick={placeOrder}
            disabled={submitting || outOfRange || distanceUnavailable || belowMinOrder || pinNotSet}
          data-testid="mobile-place-order-btn"
          className="btn-espresso flex-1 max-w-[220px] disabled:opacity-40"
        >
          {submitting ? "Placing…" : (paymentCfg?.configured ? "Pay securely" : "Place order")}
        </button>
      </div>

      <PinConfirmModal
        open={showPinConfirm}
        customer={customer}
        apiKey={mapsApiKey}
        total={total}
        submitting={submitting}
        onEdit={() => setShowPinConfirm(false)}
        onConfirm={() => placeOrder()}
      />
    </main>
  );
}
